import React from "react";
import { NexOsProvider } from "@/lib/nexOs";
import { FloatingWindowsProvider } from "@/lib/floatingWindows";
import { NdsEditorProvider } from "@/lib/ndsEditor";
import { OpsProvider } from "@/lib/opsCards";
import RecentActivityPanel from "@/components/ops/RecentActivityPanel";
import { DemoModeProvider } from "@/lib/demoMode";
import NdsHeader from "@/components/nds/NdsHeader";
import VerticalSelector from "@/components/nds/VerticalSelector";
import KpiPulseBar from "@/components/nds/KpiPulseBar";
import OpsKpiBar from "@/components/nds/OpsKpiBar";
import LiveSystemFeed from "@/components/nds/LiveSystemFeed";
import TenStepRevenueFlow from "@/components/nds/TenStepRevenueFlow";
import CapabilityRoadmap from "@/components/nds/CapabilityRoadmap";
import AccordionSection from "@/components/nds/AccordionSection";
import WindowLayer from "@/components/nds/windows/WindowLayer";
import { Section } from "@/lib/dynamicEngine";

/**
 * NexEnginesCoreLayout — the LOCKED universal template (NDS-1).
 *
 *   Tier 0 · <NdsHeader>          — brand + LIVE + env badge
 *   Tier 0 · <VerticalSelector>   — 3 permanent pill tabs
 *   Tier 1 · <KpiPulseBar>        — 4 metric cards
 *   Tier 1 · <LiveSystemFeed>     — right-rail streaming events
 *   Tier 2 · <TenStepRevenueFlow> — 2×5 grid + coaching modal
 *   Tier 3 · <CapabilityRoadmap>  — 3-col × ~7 rows of capability tiles
 *
 * Every new feature MUST fit inside this shell. Individual sections can be
 * hidden/reordered per operator via /admin/layout (Section scopes: nds_root,
 * nds_kpi, nds_step, nds_capability). Labels & schema editable at /admin/nds
 * and /admin/labels.
 */
export default function NexEnginesCoreLayout({ children, demoMode = false }) {
  return (
    <DemoModeProvider value={demoMode}>
      <NexOsProvider>
        <FloatingWindowsProvider>
          <NdsEditorProvider>
          <OpsProvider>
            <div className="nds-scope w-full" data-testid={demoMode ? "nex-engines-demo-layout" : "nex-engines-core-layout"}>
              <NdsHeader />
              <VerticalSelector />

              <main className="w-full max-w-[1920px] mx-auto px-6 lg:px-10 py-6 flex flex-col gap-4">
                {/* Tier 1 — KPI stack (real-time pulse + platform ops) inside one accordion */}
                <Section scope="nds_root" id="tier_1_kpi_feed">
                  <AccordionSection id="kpi" kicker="// TIER 1" title="Live KPIs · Real-time pulse + Platform ops">
                    <div className="flex flex-col gap-6">
                      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
                        <KpiPulseBar />
                        <LiveSystemFeed />
                      </div>
                      <OpsKpiBar />
                    </div>
                  </AccordionSection>
                </Section>

                {/* Tier 2 — 10-Step Revenue Flow */}
                <Section scope="nds_root" id="tier_2_ten_step">
                  <AccordionSection id="flow" kicker="// TIER 2" title="10-Step Revenue Flow · Universal lifecycle">
                    <TenStepRevenueFlow />
                  </AccordionSection>
                </Section>

                {/* Optional child injection (legacy dashboard blocks) */}
                {children && (
                  <Section scope="nds_root" id="tier_2_5_children">
                    <div>{children}</div>
                  </Section>
                )}

                {!demoMode && <RecentActivityPanel />}

                {/* Tier 3 — 20-Capability Roadmap */}
                <Section scope="nds_root" id="tier_3_capabilities">
                  <AccordionSection id="caps" kicker="// TIER 3" title="Modular Capabilities · 20-card roadmap">
                    <CapabilityRoadmap />
                  </AccordionSection>
                </Section>

                <footer className="border-t border-white/8 pt-6 mt-4 flex items-center justify-between text-[0.7rem] font-mono tracking-[0.2em] text-nex-muted uppercase">
                  <span>NexEngines · NDS-1 · Universal Template · v1{demoMode ? " · Sandbox" : ""}</span>
                  <span>© {new Date().getFullYear()} NexEngines</span>
                </footer>
              </main>

              {/* Floating multi-window layer — non-blocking. */}
              <WindowLayer />
            </div>
          </OpsProvider>
          </NdsEditorProvider>
        </FloatingWindowsProvider>
      </NexOsProvider>
    </DemoModeProvider>
  );
}
